import { useCallback, useEffect, useMemo, useState } from "react";
// (useEffect now also used for auto-dismiss toast)
import { AppShell, MarqueeHeader } from "../layout";
import { DateSelector, type DateRange } from "../components/DateSelector";
import { BookingGrid, BranchTabs, SumBar } from "../components/grid";
import { useAppData } from "../hooks/useAppData";
import { useBookings } from "../hooks/useBookings";
import { useGridData } from "../hooks/useGridData";
import { useVersionSpans } from "../hooks/useVersionSpans";
import { useMoveMode } from "../hooks/useMoveMode";
import { MoveModeHud } from "./dc/MoveModeHud";
import { useLeadInbox } from "../hooks/useLeadInbox";
import { useHeaderRange, mirKvDefaultRange } from "../hooks/useHeaderRange";
import { useAuth } from "../auth";
import { roleHudLabel } from "../auth/roleMap";
import type { Booking, ModalState, SlotData } from "../hooks/bookingTypes";
import { todayBusiness, toBusinessDate, fromBusinessDate, addDays, parseDateApi, fmtDateApi, slotSort, isNightSlot } from "../utils/date"; import { useGridDeeplink } from "../hooks/useGridDeeplink"; import type { NoPrepay } from "../hooks/useNoPrepay";
import { BookingModal } from "../components/modal";
import { QuestScheduleScreen } from "./dc/QuestScheduleScreen";
import { DC_TABS, DcSecNav, type DcView } from "./dc/dcTabs";   // 29.10: полоса вкладок вынесена (рэтчет), «Новый год» — вторая
import { DcNySection } from "./dc/DcNySection"; import { gridDateOf, nyReturn, type NyBooking } from "./dc/nyModel";
import { DcLeadsSection } from "./dc/DcLeadsSection";
import { DcCancellationsSection } from "./dc/DcCancellationsSection";
import { DcNoPrepaySection } from "./dc/DcNoPrepaySection";
import { DcClientsSection } from "./dc/DcClientsSection";
import { DcMirKvSection } from "./dc/DcMirKvSection";
import { DcCommerceHub } from "./dc/DcCommerceHub";
import css from "./DcGridScreen.module.css";
import { busySlotToast } from "../components/modal/blockInfo";

export type { DcView };

/**
 * B.2 — DC Booking Grid. Главный рабочий экран DC.
 *
 * Stage 2.1 минимум:
 *  - sticky top zone (Header B.2 + branch pills)
 *  - useAppData (branches/quests/slots/linked_groups) + useBookings (day+night)
 *  - merged grid with auto-blocks, conflict leads, shifted slots
 *  - SumBar в конце ленты (не sticky)
 *
 * Stage 2.2 (next): тап по слоту откроет BookingModal (B.3).
 * Stage 2.x: section-tab strip для перехода между DC-экранами.
 */
export function DcGridScreen() {
  const { user } = useAuth();
  const app = useAppData();

  /**
   * Текущий бизнес-день как UTC-midnight Date. fmtDateApi и addDays
   * читают UTC-методы, поэтому НЕЛЬЗЯ использовать `new Date(loc.y, loc.m,
   * loc.d)` — в UTC+5 это даёт прошлую дату.
   */
  const [date, setDate] = useState<Date>(() => todayBusiness());

  /* Выбранный филиал (active branch). По умолчанию — "all". */
  const [branch, setBranch] = useState<string>("all");

  /* Активный DC-раздел (section-tab strip). По умолчанию — сетка. */
  const [view, setView] = useState<DcView>("grid");
  const [nyBack] = useState(nyReturn);   // 29.10: бронь открыта из «Нового года» — после модалки вернуться туда (не после «Перенести»)

  /* Открытое многодневное расписание квеста (id) или null — обычная сетка. */
  const [scheduleQuestId, setScheduleQuestId] = useState<string | null>(null);

  /* Диапазоны календаря шапки: «Клиенты» — неделя, «Отмены» и «Юнифанс» — месяц.
     Месяц у отмен: короче — прятал бы недавние, длиннее — тянул бы весь архив. */
  const clients = useHeaderRange(date, 7);
  const cancel = useHeaderRange(date, 30);
  const commerce = useHeaderRange(date, 30);
  /* 21.09 (владелец): «Мир Кв» — период календарём; по умолчанию вчера (в понедельник — с субботы). */
  const mirkv = useHeaderRange(date, 1, mirKvDefaultRange);
  /* 21.09: подвкладка сама задала период (например «Посещаемость» — неделя) — пересобираем
     неконтролируемый календарь шапки, иначе он показывал бы старый диапазон. */
  const [seed, setSeed] = useState(0);

  /* Modal / move-mode / toast state. */
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string>("");

  const { bookings, loading, error, newIds, save, reload } = useBookings(date);
  /* A48-коррекция: «Перенести» = move-режим ЭТОЙ сетки (баннер + тап слота). */
  const mm = useMoveMode(setToast, reload);
  const leadInbox = useLeadInbox();
  const versions = useVersionSpans();   // 27.09: спецверсии держат свою длительность — как гейт
  const { merged, conflictLeads, questRows } = useGridData(
    bookings, app.quests, app.slots, app.linkedGroups, app.branches, branch, app.masterClasses, versions,
  );

  useEffect(() => {
    document.documentElement.dataset.role = "dc";
  }, []);

  const onDateChange = useCallback((value: Date | DateRange) => {
    /* DateSelector emits local-zone Date (day) или {start,end} (range);
       нормализуем в business UTC-midnight (так читает fmtDateApi). */
    if (value instanceof Date) { setDate(toBusinessDate(value)); return; }
    const range = {
      start: value.start ? toBusinessDate(value.start) : null,
      end: value.end ? toBusinessDate(value.end) : null,
    };
    if (view === "commerce") commerce.set(range);
    else if (view === "cancel") cancel.set(range);
    else if (view === "mirkv") mirkv.set(range);
    else clients.set(range);
  }, [view, clients, cancel, commerce, mirkv]);

  const isClients = view === "clients";
  const isCommerce = view === "commerce";
  /* 11.09 (Даша через владельца): отмены ищут по периоду — календарь шапки фильтрует список. */
  const isCancel = view === "cancel";
  const isMirKv = view === "mirkv";

  const onSlotClick = useCallback(
    (questId: string, baseTime: string, data: SlotData | null, conflict: Booking | null, presetTime?: string | null, clash?: string | null) => {
      if (mm.booking) {
        if (mm.moving || (data && data.type !== "auto-block")) { if (!mm.moving) setToast(busySlotToast(data as Booking)); return; }   // 28.09: у блока — причина; 12.10: автоблок пускаем — гейт предложит «всё равно»
        void mm.dropOnSlot(questId, baseTime, fmtDateApi(date), presetTime ?? null);   // 17.09: сдвинутый слот → 17:45
        return;
      }
      setModal({
        questId,
        time: baseTime,
        booking: data && data.type !== "auto-block" ? (data as Booking) : null,
        conflictLead: conflict,
        isNew: !data,
        customTime: !data && presetTime ? presetTime : undefined,   // 17.09: слот сдвинут МК (04.10: и чайной) → бронь на сдвинутое время
        clash: data && clash ? clash : undefined,                     // 17.09: игра стоит на сдвинутом слоте (04.10: или блок «Чайная» лишний) — плашка в модалке
      });
    },
    [mm, date, setModal],
  );

  /* Auto-dismiss toast after 2.5s. */
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(""), 2500);
    return () => window.clearTimeout(id);
  }, [toast]);

  const dataReady = !app.loading && !loading;
  const { dlClient, clearDlClient, openBooking } = useGridDeeplink(dataReady, merged, modal, setModal, setDate); // TG-диплинк (24.08)
  const movingId = mm.booking?.id ?? null; const userName = user?.nickname || user?.name || "guest";
  /* Тап по «БЕЗ предоплаты» (24.08, владелец): в сетку дня брони + модалка через приземление диплинка; ночной 00:xx — грид D-1 */
  const openNoPrepay = (b: NoPrepay) => { openBooking(Number(b.id), isNightSlot((b.time || "").slice(0, 5)) ? fmtDateApi(addDays(parseDateApi(b.date), -1)) : b.date); setView("grid"); setScheduleQuestId(null); };
  const openNy = (b: NyBooking) => { openBooking(b.id, gridDateOf(b)); nyBack.open(b.id); setView("grid"); setScheduleQuestId(null); };   // 29.10: так же, день сетки (ночной — D-1) отдаёт сервер

  const sumStats = useMemo(() => merged, [merged]);

  return (
    <AppShell
      header={
        <MarqueeHeader
          roleLabel={roleHudLabel("DC")}
          nick={userName}
          dateSelector={view === "ny" ? null /* 29.10: у «Нового года» свой сезон — дневной календарь сетки не показываем */
            : (isClients || isCommerce || isCancel || isMirKv) ? (
              <DateSelector
                key={isCommerce ? `dc-commerce-range-${seed}` : isCancel ? "dc-cancel-range" : isMirKv ? "dc-mirkv-range" : "dc-range"}
                mode="range"
                initialRange={isCommerce ? commerce.local : isCancel ? cancel.local : isMirKv ? mirkv.local : clients.local}
                onChange={onDateChange}
              />
            ) : (
              <DateSelector
                key={`dc-day-${fmtDateApi(date)}`} /* селектор неконтролируемый: ремоунт при внешней смене дня (диплинк, «БЕЗ») */
                mode="day"
                initialDate={fromBusinessDate(date)}
                onChange={onDateChange}
              />
            )
          }
        />
      }
      topZone={
        <>
          <DcSecNav
            view={view}
            onChange={(v) => { setView(v); setScheduleQuestId(null); nyBack.drop(); }}
            leadCount={leadInbox.leads.length}
          />
          {view === "grid" && !scheduleQuestId ? (
            <BranchTabs
              branches={app.branches.filter((b) => b.active)}
              active={branch}
              onChange={setBranch}
            />
          ) : null}
        </>
      }
    >
      {view !== "grid" ? (
        <DcSection
          view={view}
          date={date}
          branch={branch}
          leadInbox={leadInbox}
          onToast={setToast} onOpenNoPrepay={openNoPrepay} onOpenNy={openNy}
          clientsRange={clients.eff}
          commerceRange={commerce.eff}
          cancelRange={cancel.eff}
          mirkvRange={mirkv.eff}
          onCommerceRange={(f, t) => { commerce.set({ start: parseDateApi(f), end: parseDateApi(t) }); setSeed((n) => n + 1); }}
        />
      ) : scheduleQuestId ? (
        <QuestScheduleScreen
          questId={scheduleQuestId}
          questName={app.quests.find((q) => q.id === scheduleQuestId)?.name}
          onBack={() => setScheduleQuestId(null)}
          onPickDate={(dateStr) => {
            setDate(parseDateApi(dateStr));
            setScheduleQuestId(null);
          }}
        />
      ) : app.error ? (
        <div className={css.err}>Ошибка загрузки справочников: {app.error}</div>
      ) : error ? (
        <div className={css.err}>Ошибка загрузки бронирований: {error}</div>
      ) : !dataReady ? (
        <div className={css.loading}>Загрузка…</div>
      ) : (
        <>
          <MoveModeHud mm={mm} />
          <BookingGrid
            rows={questRows}
            bookings={merged}
            conflicts={conflictLeads}
            moveMode={!!mm.booking}
            movingId={movingId}
            newIds={newIds}
            onSlotClick={onSlotClick}
            onQuestClick={setScheduleQuestId}
          />
          <SumBar bookings={sumStats} onReload={reload} />
        </>
      )}

      {modal ? (
        <BookingModal
          questId={modal.questId}
          time={modal.time}
          curDate={date}
          booking={modal.booking}
          conflictLead={modal.conflictLead}
          initialCustomTime={modal.customTime}
          clash={modal.clash}
          dayBookings={merged}
          questSlots={app.slots
            .filter((s) => s.quest_id === modal.questId && s.active)
            .map((s) => s.time)
            .sort(slotSort)}
          onSave={save}
          onClose={() => { setModal(null); clearDlClient(); if (nyBack.close(modal.booking?.id)) setView("ny"); }}
          onToast={setToast}
          onReload={reload}
          onStartMove={nyBack.moving(mm.start)} initialShowInfo={dlClient}
        />
      ) : null}

      {toast ? <div className={css.toast}>{toast}</div> : null}
    </AppShell>
  );
}

/* ── Non-grid DC sections. Real screens land per-section (04..08); until then
 * a labelled placeholder keeps the role navigable. ──────────────────────── */
function DcSection({ view, date, leadInbox, onToast, clientsRange, commerceRange, cancelRange, mirkvRange, onCommerceRange, onOpenNoPrepay, onOpenNy }: { view: DcView; date: Date; branch: string; leadInbox: ReturnType<typeof useLeadInbox>; onToast: (m: string) => void; clientsRange: DateRange; commerceRange: DateRange; cancelRange: DateRange; mirkvRange: DateRange; onCommerceRange: (from: string, to: string) => void; onOpenNoPrepay: (b: NoPrepay) => void; onOpenNy: (b: NyBooking) => void }) {
  if (view === "ny") return <DcNySection onOpen={onOpenNy} onToast={onToast} />;   /* 29.10: «Новый год» */
  if (view === "leads") return <DcLeadsSection leads={leadInbox.leads} loading={leadInbox.loading} error={leadInbox.error} onChanged={leadInbox.reload} onToast={onToast} />;   /* 15.10: «Обработан» */
  if (view === "cancel") {
    /* 11.09: период из календаря шапки; fmtDateApi даёт формат, который ждёт сервер. */
    return <DcCancellationsSection onToast={onToast}
      from={cancelRange.start ? fmtDateApi(cancelRange.start) : null}
      to={cancelRange.end ? fmtDateApi(cancelRange.end) : null} />;
  }
  if (view === "bez") return <DcNoPrepaySection onOpen={onOpenNoPrepay} />;
  if (view === "clients") return <DcClientsSection range={clientsRange} />;
  if (view === "mirkv") return <DcMirKvSection range={mirkvRange} />;
  if (view === "commerce") return <DcCommerceHub onToast={onToast} from={commerceRange.start ? fmtDateApi(commerceRange.start) : undefined} to={commerceRange.end ? fmtDateApi(commerceRange.end) : undefined} onSetRange={onCommerceRange} />;
  const label = DC_TABS.find((t) => t.id === view)?.label ?? "";
  return (
    <div className={css.sectionStub}>
      <div className={css.sectionStubInner}>
        <div className={css.sectionStubTitle}>В разработке</div>
        <div className={css.sectionStubSub}>{label}</div>
      </div>
    </div>
  );
}
